// utils/tax-categorizer.ts
type RecordLike = {
  from_code?: string | null; // ex: 'BRSSZ'
  to_code?: string | null;   // ex: 'CNSHA'
};

type TaxLike = {
  category?: string | null;       // pode vir 'Origin' | 'Freight' | 'Destination' | ...
  category_name?: string | null;  // ex: 'Taxas de origem'
  currency?: string | null;
  value?: number | string | null;
};

// ====================== Helpers ======================
const normalize = (s?: string | null) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove marcas de acento
    .toLowerCase()
    .trim();

/**
 * Converte string/number para float (locale-agnostic)
 */
function parseToFloat(val: any): number {
  if (val == null) return 0;
  if (typeof val === 'number') return isFinite(val) ? val : 0;

  let str = String(val).trim();
  str = str.replace(/[^0-9,.\-+]/g, '');

  const hasComma = str.includes(',');
  const hasDot = str.includes('.');

  if (hasComma && hasDot) {
    const lastComma = str.lastIndexOf(',');
    const lastDot = str.lastIndexOf('.');
    if (lastComma > lastDot) {
      str = str.replace(/\./g, '').replace(/,/g, '.');
    } else {
      str = str.replace(/,/g, '');
    }
  } else if (hasComma && !hasDot) {
    str = str.replace(/,/g, '.');
  }

  const num = parseFloat(str);
  return isNaN(num) ? 0 : num;
}

// ====================== Currency heuristics ======================
const countryCurrencyMap: Record<string, string> = {
  BR: 'brl', US: 'usd', DE: 'eur', FR: 'eur', ES: 'eur', IT: 'eur', NL: 'eur',
  PT: 'eur', BE: 'eur', CN: 'cny', JP: 'jpy', GB: 'gbp', IN: 'inr', CA: 'cad',
  MX: 'mxn', CL: 'clp', AR: 'ars', CO: 'cop', PE: 'pen', KR: 'krw', SG: 'sgd',
  AU: 'aud',
};

const getCountry = (loc?: string | null) =>
  (loc && loc.length >= 2 ? loc.slice(0, 2).toUpperCase() : '');

const getCurrencyByCountry = (cc: string) => countryCurrencyMap[cc] || '';

const currencyHeuristic = (
  currency: string,
  record: RecordLike
): 'origin' | 'destination' | null => {
  const cur = normalize(currency);
  const originCountry = getCountry(record.from_code);
  const destCountry = getCountry(record.to_code);
  const originCur = getCurrencyByCountry(originCountry);
  const destCur = getCurrencyByCountry(destCountry);

  if (originCur && cur === originCur) return 'origin';
  if (destCur && cur === destCur) return 'destination';
  return null;
};

// ====================== Patterns ======================
const ORIGIN_MARKERS = [
  /\b(origin|export|pol|origem|saida)\b/i,
  /\b\(o\)|\(origin\)|\(pol\)/i,
];

const DEST_MARKERS = [
  /\b(destination|import|pod|destino|entrada)\b/i,
  /\b\(d\)|\(dest\)|\(pod\)/i,
];

const FREIGHT_PATTERNS = [
  /\bbasic\s+ocean\s+freight\b/i,
  /\bocean\s*freight\b/i,
  /\bfreight\b/i,
  /\bmaersk\s*spot\b/i,
  /\bbaf\b/i,
  /\bcaf\b/i,
  /\bp(ss|eak)\s*season\b/i,
  /\bemergency\b/i,
  /\binsurance\b/i,
  /\bbrc\b/i,
  /\bbunker\s+recovery\s+charge\b/i,
  /\bcsf\b/i,
  /\bcarrier\s+security\s+fee\b/i,
];

const ORIGIN_LOCAL_PATTERNS = [
  /\bterminal\s*handling.*origin\b/i,
  /\borigin\s*thc\b/i,
  /\bdocumentation\s*fee.*origin\b/i,
  /\bexport\s*service\b/i,
  /\bgate\s*fee\b/i,
  /\bvbs\b/i,
  /\bwharfage\b/i,
  /\bseal\s*fee\b/i,
];

const DEST_LOCAL_PATTERNS = [
  /\bterminal\s*handling.*destination\b/i,
  /\bcontainer\s*protect\b/i,
  /\bdestination\s*thc\b/i,
  /\bdocumentation\s*fee.*destination\b/i,
  /\bimport\s*service\b/i,
  /\bdelivery\s*order\b|\bd\/?o\b/i,
  /\bunloading\s*charge\b/i,
  /\bdeconsolidation\b/i,
  /\bcfs\b/i,
];

const PENALTY_OR_TIME_PATTERNS = [
  /\bdetention\b/i,
  /\bdemurrage\b/i,
  /\bstorage\b/i,
  /\bpenalty\b|\bpenalty\s*fees?\b/i,
  /\bamendment\b/i,
  /\bno-?show\b/i,
  /\bcancellation\b/i,
  /\brollover\b/i,
  /\bfree\s*time\b|\bfreetime\b/i,
];

const matchesAny = (text: string, patterns: RegExp[]) =>
  patterns.some((r) => r.test(text));

// ====================== category_name mapper ======================
const mapCategoryName = (category?: string | null):
  | 'freight'
  | 'origin'
  | 'destination'
  | 'penalty'
  | 'other'
  | null => {
  const c = normalize(category);
  if (!c) return null;
  if (/\btaxas?\s*de\s*frete\b|\bfreight\b/.test(c)) return 'freight';
  if (/\btaxas?\s*de\s*origem\b|\borigin\b|\bexport\b/.test(c)) return 'origin';
  if (/\btaxas?\s*de\s*destino\b|\bdestination\b|\bimport\b/.test(c)) return 'destination';
  if (/\bpenalidade\b|\bpenalty\b|\bfreetime\b|\bfree\s*time\b/.test(c)) return 'penalty';
  return 'other';
};

// ====================== Trusted raw categories & Panels ======================
const TRUSTED_RAW = new Set(['origin', 'freight', 'destination'] as const);

type PanelTitle =
  | 'Frete e taxas que acompanham frete'
  | 'Taxas locais da origem'
  | 'Taxas locais do destino'
  | 'Outras taxas';

const PANEL_BY_TRUSTED: Record<'origin' | 'freight' | 'destination', PanelTitle> = {
  origin: 'Taxas locais da origem',
  freight: 'Frete e taxas que acompanham frete',
  destination: 'Taxas locais do destino',
};

// ====================== Main ======================
export function categorizeTax(
  taxName: string,
  taxCurrency: string,
  record: RecordLike,
  tax?: TaxLike | null
): PanelTitle | null {
  // 0) normaliza o nome para reuso
  const nameNorm = normalize(taxName);

  // 0.1) descarta TOTALMENTE "Bunker recovery charge [BRC]"
  // if (/\bbunker\s*recovery\s*charge\b/i.test(nameNorm) && /\bbrc\b/i.test(nameNorm)) {
  //   return null; // <- sinaliza que não deve aparecer
  // }

  // 1) garante número (sem modificar o objeto original)
  const taxValue = tax ? parseToFloat(tax.value) : 0;

  // 2) category cru
  const rawCatNormalized = normalize(tax?.category);

  // 3) raw confiável - NÃO modificar o objeto original
  if (rawCatNormalized && TRUSTED_RAW.has(rawCatNormalized as any)) {
    return PANEL_BY_TRUSTED[rawCatNormalized as keyof typeof PANEL_BY_TRUSTED];
  }

  // 4) category_name mapeável
  const mapped = mapCategoryName(tax?.category_name);
  if (mapped === 'freight') return 'Frete e taxas que acompanham frete';
  if (mapped === 'origin') return 'Taxas locais da origem';
  if (mapped === 'destination') return 'Taxas locais do destino';
  if (mapped === 'penalty') return 'Outras taxas';

  // 5) padrões explícitos
  if (matchesAny(nameNorm, FREIGHT_PATTERNS)) return 'Frete e taxas que acompanham frete';
  if (matchesAny(nameNorm, PENALTY_OR_TIME_PATTERNS)) return 'Outras taxas';

  // 6) padrões locais
  if (matchesAny(nameNorm, ORIGIN_MARKERS) || matchesAny(nameNorm, ORIGIN_LOCAL_PATTERNS)) {
    return 'Taxas locais da origem';
  }
  if (matchesAny(nameNorm, DEST_MARKERS) || matchesAny(nameNorm, DEST_LOCAL_PATTERNS)) {
    return 'Taxas locais do destino';
  }

  // 7) heurística de moeda
  const coinHint = currencyHeuristic(taxCurrency, record);
  if (coinHint === 'origin') return 'Taxas locais da origem';
  if (coinHint === 'destination') return 'Taxas locais do destino';

  // 8) regras fracas
  if (/documentation\s*fee/i.test(nameNorm)) return 'Taxas locais da origem';
  if (/terminal\s*handling\s*service|thc/i.test(nameNorm)) {
    return coinHint === 'destination' ? 'Taxas locais do destino' : 'Taxas locais da origem';
  }

  // 9) fallback
  return 'Outras taxas';
}

// Nova função para obter a descrição correta
export function getTaxDescription(tax: TaxLike | null, record: RecordLike): string {
  if (!tax) return '';

  // Para registros importados, usar category_name se disponível
  if ((record as any).import && tax.category_name) {
    return tax.category_name;
  }

  if (tax.category === "Origin" || tax.category === 'Destination') {
    return tax.category_name;
  }
  // Para registros não importados ou quando category_name não existe, usar category
  return tax.category || '';
}
